import { useEffect, useMemo, useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ReactComponent as MenuIcon } from '../../../../icons/svg/menu.svg';
import { ReactComponent as LogoIcon } from '../../../../icons/svg/logo-new-black.svg';
import { ReactComponent as LikeIcon } from '../../../../icons/svg/like.svg';
import { ReactComponent as UserIcon } from '../../../../icons/svg/user.svg';
import { ReactComponent as InboxIcon } from '../../../../icons/svg/message.svg';
import { ReactComponent as OrdersIcon } from '../../../../icons/svg/cart.svg';
import { ReactComponent as NotificationsIcon } from '../../../../icons/svg/bell.svg';
import { ReactComponent as LogoutIcon } from '../../../../icons/svg/logout.svg';
import { ReactComponent as FolderIcon } from '../../../../icons/svg/folder.svg';
import { ReactComponent as HouseIcon } from '../../../../icons/svg/house.svg';
import { Badge } from '../../../../components/badge';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../../../../components/popover';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from '../../../../components/avatar';
import { UserMenuElement } from './UserMenuElement';
import type { HeaderNavLinks, HeaderUserData } from '../Header';

const _desktopUserMenuTriggerVariants = cva('flex items-center gap-x-3', {
    variants: {
        color: {
            light: 'text-white',
            dark: 'text-gray-900',
        },
    },
    defaultVariants: {
        color: 'light',
    },
});

const _desktopUserMenuIconVariants = cva('size-5', {
    variants: {
        color: {
            light: 'fill-white',
            dark: 'fill-gray-900',
        },
    },
    defaultVariants: {
        color: 'dark',
    },
});

interface DesktopUserMenuProps
    extends VariantProps<typeof _desktopUserMenuTriggerVariants> {
    userData: HeaderUserData;
    onInboxClick?: () => void;
    onLogout: () => void;
    additionalUserMenuItems?: HeaderNavLinks;
    currentCountryCode?: string;
    /**
     * Optional location object from Remix Router (pathname, search).
     * When provided, the menu will auto-close on navigation.
     * Used in Remix contexts; omit for non-Remix contexts (e.g., Business Manager).
     */
    location?: { pathname: string; search: string } | null;
}

export const DesktopUserMenu = (props: DesktopUserMenuProps) => {
    const {
        color,
        userData,
        onInboxClick,
        onLogout,
        additionalUserMenuItems,
        currentCountryCode,
        location,
    } = props;

    const {
        avatarSrc = '',
        firstName,
        lastName = '',
        notificationsCount,
        professionalSections,
        showCountryLabels,
    } = userData;

    const [open, setOpen] = useState(false);
    const initials = useMemo(() => {
        return `${firstName[0]?.toUpperCase() ?? ''}${lastName[0]?.toUpperCase() ?? ''}`;
    }, [firstName, lastName]);

    const shouldShowMyProjects = userData.isBuildHubEnabled;
    const isUS = currentCountryCode === 'US';

    // Close popover when location changes (e.g., after navigation)
    // Only runs when location prop is provided (Remix contexts)
    useEffect(() => {
        if (location) {
            setOpen(false);
        }
    }, [location]);

    return (
        <div className="relative hidden items-center md:flex">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger>
                    <div className={_desktopUserMenuTriggerVariants({ color })}>
                        <MenuIcon
                            className={_desktopUserMenuIconVariants({ color })}
                        />
                        <div className="relative flex items-center">
                            <Avatar size="square64">
                                <AvatarImage src={avatarSrc} />
                                <AvatarFallback>{initials}</AvatarFallback>
                            </Avatar>
                            {notificationsCount > 0 ? (
                                <div className="absolute -right-1 -top-1 flex">
                                    <Badge
                                        size="centeredCompact"
                                        text={
                                            notificationsCount > 9
                                                ? 'small'
                                                : 'default'
                                        }
                                        color={'default'}
                                    >
                                        {notificationsCount > 9
                                            ? '9+'
                                            : notificationsCount}
                                    </Badge>
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>
                </PopoverTrigger>
                {/* forceMount keeps the menu content in DOM to prevent logo refetching when reopening */}
                <PopoverContent
                    paddings="small"
                    align="end"
                    className="max-w-64"
                    forceMount
                >
                    <div className="flex flex-col">
                        <div className="flex h-8 items-center px-2">
                            <span className="text-sm font-medium">
                                {firstName} {lastName}
                            </span>
                        </div>
                        <UserMenuElement
                            icon={
                                <UserIcon className="mx-auto size-4.5 stroke-gray-900" />
                            }
                            title="My Account"
                            to="/member/settings/edit-profile"
                            external={true}
                        />
                        <UserMenuElement
                            icon={<InboxIcon />}
                            title="Inbox"
                            to="/member/inbox"
                            external={true}
                            badgeCount={userData.inboxCount}
                            onClick={
                                onInboxClick
                                    ? () => {
                                          onInboxClick();
                                          setOpen(false);
                                      }
                                    : undefined
                            }
                        />
                        {shouldShowMyProjects ? (
                            <UserMenuElement
                                icon={<HouseIcon />}
                                title="My Projects"
                                to="/app/my-projects"
                            />
                        ) : null}
                        <UserMenuElement
                            icon={<LikeIcon />}
                            title="Design Boards"
                            to="/member/design-boards"
                        />
                        <UserMenuElement
                            icon={<FolderIcon />}
                            title="Guides"
                            to="/app/guides"
                        />
                        <UserMenuElement
                            icon={<OrdersIcon />}
                            title="Orders"
                            to="/member/orders"
                            external={true}
                            badgeCount={userData.orderNotificationCount}
                        />
                        <UserMenuElement
                            icon={<NotificationsIcon />}
                            title="Notifications"
                            to="/member/notifications"
                            external={true}
                            badgeCount={userData.websiteNotificationCount}
                        />
                        {professionalSections.length > 0 ? (
                            <>
                                <div className="my-2.5 h-[1px] border-t-0 bg-gray-400"></div>
                                {professionalSections.map((section) => (
                                    <div key={section.country}>
                                        {showCountryLabels && (
                                            <div className="px-2 pb-2 pt-2">
                                                <span className="text-sm font-medium">
                                                    {section.label}
                                                </span>
                                            </div>
                                        )}
                                        {section.professionals.map(
                                            (professional) => {
                                                const logoSrc =
                                                    professional.companyLogo48Resized ||
                                                    professional.logo;
                                                return (
                                                    <UserMenuElement
                                                        key={professional.id}
                                                        icon={
                                                            <Avatar
                                                                size="square20"
                                                                className="p-0.5"
                                                                style={{
                                                                    backgroundColor:
                                                                        professional.logoBackgroundColor ??
                                                                        (logoSrc
                                                                            ? 'white'
                                                                            : '#C7C7C7'),
                                                                }}
                                                            >
                                                                <AvatarImage
                                                                    src={
                                                                        logoSrc
                                                                    }
                                                                    fit="contain"
                                                                />
                                                                <AvatarFallback className="bg-transparent" />
                                                            </Avatar>
                                                        }
                                                        title={
                                                            professional.title
                                                        }
                                                        to={`/business/${professional.urlSegment}`}
                                                        external={true}
                                                    />
                                                );
                                            }
                                        )}
                                    </div>
                                ))}
                            </>
                        ) : null}
                        <div className="my-2.5 h-[1px] border-t-0 bg-gray-400"></div>
                        <UserMenuElement
                            icon={<LogoIcon />}
                            title="List a Business"
                            to="/advertising"
                            external={true}
                        />
                        {!isUS && (
                            <div className="my-2.5 h-[1px] border-t-0 bg-gray-400"></div>
                        )}
                        {!isUS && (
                            <UserMenuElement
                                icon={<LogoIcon />}
                                title="Home Design Evening"
                                to="/home-design-event"
                                external={true}
                            />
                        )}
                        {additionalUserMenuItems &&
                        additionalUserMenuItems.links &&
                        additionalUserMenuItems.links.length > 0 ? (
                            <>
                                <div className="my-2.5 h-[1px] border-t-0 bg-gray-400"></div>
                                {additionalUserMenuItems.links.map((item) => (
                                    <UserMenuElement
                                        key={item.title}
                                        icon={<LogoIcon />}
                                        title={item.title}
                                        {...(item.onClick
                                            ? { onClick: item.onClick }
                                            : { to: item.to })}
                                        external={item.external}
                                    />
                                ))}
                            </>
                        ) : null}
                        <div className="my-2.5 h-[1px] border-t-0 bg-gray-400"></div>
                        <UserMenuElement
                            icon={<LogoutIcon />}
                            title="Logout"
                            onClick={onLogout}
                        />
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};
